import * as THREE from "three";

// World streaming (from the superposition spike's vertical-slice stream 8, gate 8): chunks load and unload by distance
// from the player, built in small steps under a per-frame time budget, warmed on the GPU before they show, and disposed
// when they go. It follows the DYSTOPIA streamer's contract (ai-context/references/dystopia-teardown-notes/
// world-streaming.md §2, card B), cut down to one tier on the main thread. The game supplies the chunks (`ChunkSpec`:
// an area and its build steps); this file knows nothing about what they hold.
//
// - Radii (rectangle distance from the player to a chunk's walkable area): `request` queues a build, `inBy` is where it
//   must already be live (missing that counts as late), `unload` (> request) drops it, so a chunk on the edge doesn't
//   thrash. Two more anchors ask early: the player `predictS` ahead and an optional view point the caller passes (so
//   what the camera looks down is there before she walks it); only the player herself can make a chunk late.
// - A slice per frame: drops first, then builds, chunks inside `inBy` first, then nearest. Steps run while the slice
//   is under `budgetMs` (at least one a frame); a late chunk gets `lateBudgetMs`. Every step should be small by design:
//   repeated props come from shared kits (kits.ts, `createKitCache`), never built in a slice.
// - Warm before show (the last step): the chunk joins the scene on a layer the main camera doesn't see, its programs
//   are compiled (`renderer.compile`), its textures uploaded (`initTexture`), and it is drawn once into a 1×1 target so
//   its buffers upload too; then it moves to layer 0. The first frame that shows it does no upload or compile work, and
//   the slice's measured time includes that work.
// - Dispose on drop: geometries, materials and textures the chunk made (anything marked `userData.resident` is shared
//   and kept), and instanced meshes' buffers. A material whose program has no other user would take the program with
//   it, and the next load would recompile (a hitch), so the first material seen per program is kept as that program's
//   pin (its textures are still freed): the program count stays flat however often chunks come and go.
// - prime(): blocking, at boot (every chunk is built and warmed once, so every program the world can use is compiled
//   before play, then the far ones are dropped) and on a teleport (a "cut", as a warp or a respawn is, with a grace
//   window before lateness counts). Slices and cuts are measured separately: `stats()`.
// - Faults: `skipDispose` drops chunks without disposing anything (the memory check must catch it).

import { areaDistance, type Area } from "./area";

export { areaDistance, type Area } from "./area";

export interface BuildStep {
  name: string;
  run: () => void;
}

export interface ChunkBuild {
  /** The chunk's root, not in the scene until the warm step attaches it. */
  group: THREE.Group;
  /** Register a texture the chunk made (disposed with it; one marked `userData.resident` is kept). */
  own(t: THREE.Texture): void;
}

export interface ChunkSpec {
  id: string;
  area: Area;
  /** The chunk's build as small steps, run in order across frames. */
  steps(b: ChunkBuild): BuildStep[];
  /** When it goes live (`instant`: primed, no fade-in) and when it is dropped. */
  onLive?(instant: boolean): void;
  onDrop?(): void;
}

export interface StreamConfig {
  request: number;
  inBy: number;
  unload: number;
  budgetMs: number;
  lateBudgetMs: number;
  /** Seconds of velocity for the predicted anchor. */
  predictS: number;
  /** A jump of more than this many metres between updates is a teleport (a cut). */
  teleportM: number;
  /** Frames after a cut before lateness counts. */
  graceFrames: number;
}

export const STREAM_DEFAULTS: StreamConfig = { request: 85, inBy: 60, unload: 100, budgetMs: 2, lateBudgetMs: 6, predictS: 1.5, teleportM: 12, graceFrames: 120 };

type State = "queued" | "building" | "live";
interface Entry {
  spec: ChunkSpec;
  state: State;
  group: THREE.Group;
  textures: Set<THREE.Texture>;
  steps: BuildStep[];
  i: number;
  late: boolean;
}

/** One measured slice: its main-thread time and the steps it ran. */
interface Slice {
  frame: number;
  ms: number;
  steps: string[];
}

const WARM_LAYER = 31;
const r2 = (v: number): number => Math.round(v * 100) / 100;


/** Loads, warms and drops a game's chunks around the player under a per-frame budget; `stats()` measures it. */
export class Streamer {
  readonly cfg: StreamConfig;
  readonly faults = { skipDispose: false };
  private entries = new Map<string, Entry>();
  private drops: Entry[] = [];
  private pins = new Map<string, THREE.Material>();
  private warmRT = new THREE.WebGLRenderTarget(1, 1);
  private warmCam = new THREE.PerspectiveCamera();
  private last: { x: number; z: number } | null = null;
  private grace = 0;
  private frame = 0;
  private primed = false;
  // measurements
  private slices: Slice[] = [];
  private cuts: { frame: number; ms: number; reason: string; built: string[] }[] = [];
  private lateLog: { frame: number; id: string; d: number; state: string }[] = [];
  private counts = { loads: 0, unloads: 0, late: 0, steps: 0 };
  private maxStep = { ms: 0, name: "" };
  /** The last warms' breakdown (compile, texture upload, the 1×1 draw), ms. */
  private warms: { id: string; compile: number; upload: number; draw: number }[] = [];

  constructor(
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    private camera: THREE.Camera,
    private specs: ChunkSpec[],
    cfg: Partial<StreamConfig> = {},
  ) {
    this.cfg = { ...STREAM_DEFAULTS, ...cfg };
  }

  /** Blocking build of what the hero at (x, z) needs. `all`: build and warm every chunk first (boot), then drop the
   *  ones outside the request radius, so every program is compiled before play. */
  prime(x: number, z: number, opts: { all?: boolean; reason?: string; view?: { x: number; z: number } } = {}): void {
    const dist = (a: Area): number => Math.min(areaDistance(a, x, z), opts.view ? areaDistance(a, opts.view.x, opts.view.z) : Infinity);
    const t0 = performance.now();
    const built: string[] = [];
    if (!this.primed) this.renderer.initRenderTarget(this.warmRT); // (its texture is counted before any baseline)
    for (const spec of this.specs) {
      const d = dist(spec.area);
      const want = d <= this.cfg.request;
      const e = this.entries.get(spec.id);
      if (!want && !opts.all) {
        if (e && d > this.cfg.unload) this.drop(e);
        continue;
      }
      const entry = e ?? this.queue(spec);
      if (entry.state !== "live") {
        const c0 = performance.now();
        let slow = { ms: 0, name: "" };
        while ((entry.state as State) !== "live") {
          const s0 = performance.now();
          const name = this.step(entry, true);
          if (performance.now() - s0 > slow.ms) slow = { ms: performance.now() - s0, name };
        }
        built.push(`${spec.id} ${r2(performance.now() - c0)} ms (slowest: ${slow.name} ${r2(slow.ms)})`);
      }
    }
    if (opts.all) for (const e of [...this.entries.values()]) if (dist(e.spec.area) > this.cfg.request) this.drop(e);
    for (const e of this.drops.splice(0)) this.dispose(e);
    this.last = { x, z };
    this.grace = this.cfg.graceFrames;
    this.primed = true;
    this.cuts.push({ frame: this.frame, ms: r2(performance.now() - t0), reason: opts.reason ?? (opts.all ? "boot" : "prime"), built });
  }

  /** One frame's slice. (x, z) the hero, (vx, vz) her velocity. */
  update(x: number, z: number, vx = 0, vz = 0, view?: { x: number; z: number }): void {
    this.frame++;
    if (this.grace > 0) this.grace--;
    if (this.last && Math.hypot(x - this.last.x, z - this.last.z) > this.cfg.teleportM) {
      this.prime(x, z, { reason: "teleport", view });
      return;
    }
    this.last = { x, z };
    const px = x + vx * this.cfg.predictS;
    const pz = z + vz * this.cfg.predictS;
    // scan: queue what's wanted, drop what's past the unload radius
    let late = false;
    for (const spec of this.specs) {
      const dh = areaDistance(spec.area, x, z);
      const d = Math.min(dh, areaDistance(spec.area, px, pz), view ? areaDistance(spec.area, view.x, view.z) : Infinity);
      const e = this.entries.get(spec.id);
      if (!e) {
        if (d <= this.cfg.request) this.queue(spec);
        continue;
      }
      if (d > this.cfg.unload) {
        this.drop(e);
        continue;
      }
      if (e.state !== "live" && dh <= this.cfg.inBy) {
        late = true;
        if (!e.late && this.grace === 0) {
          e.late = true;
          this.counts.late++;
          if (this.lateLog.length < 32) this.lateLog.push({ frame: this.frame, id: spec.id, d: r2(dh), state: `${e.state} ${e.i}/${e.steps.length}` });
        }
      }
    }
    // work
    const t0 = performance.now();
    const budget = late ? this.cfg.lateBudgetMs : this.cfg.budgetMs;
    const ran: string[] = [];
    while (ran.length === 0 || performance.now() - t0 < budget) {
      const drop = this.drops.shift();
      if (drop) {
        this.dispose(drop);
        ran.push(`${drop.spec.id}:dispose`);
        continue;
      }
      const next = this.nextBuild(x, z);
      if (!next) break;
      ran.push(`${next.spec.id}:${this.step(next, false)}`);
    }
    if (ran.length) this.slices.push({ frame: this.frame, ms: performance.now() - t0, steps: ran });
    if (this.slices.length > 20000) this.slices.splice(0, 10000);
  }

  private queue(spec: ChunkSpec): Entry {
    const group = new THREE.Group();
    group.name = `chunk:${spec.id}`;
    const textures = new Set<THREE.Texture>();
    const e: Entry = { spec, state: "queued", group, textures, steps: [], i: 0, late: false };
    e.steps = [...spec.steps({ group, own: (t) => void textures.add(t) }), { name: "warm", run: () => this.warm(e) }];
    this.entries.set(spec.id, e);
    return e;
  }

  /** The building chunk to step next: inside `inBy` first, then the nearest. */
  private nextBuild(x: number, z: number): Entry | null {
    let best: Entry | null = null;
    let bestKey = Infinity;
    for (const e of this.entries.values()) {
      if (e.state === "live") continue;
      const d = areaDistance(e.spec.area, x, z);
      const key = (d <= this.cfg.inBy ? 0 : 1e6) + d;
      if (key < bestKey) {
        bestKey = key;
        best = e;
      }
    }
    return best;
  }

  /** Run one step of `e`'s build; returns its name. */
  private step(e: Entry, primed: boolean): string {
    e.state = "building";
    const s = e.steps[e.i++]!;
    const t0 = performance.now();
    s.run();
    const ms = performance.now() - t0;
    this.counts.steps++;
    if (!primed && ms > this.maxStep.ms) this.maxStep = { ms, name: `${e.spec.id}:${s.name}` };
    if (e.i >= e.steps.length) {
      e.state = "live";
      e.late = false;
      this.counts.loads++;
      e.spec.onLive?.(primed);
    }
    return s.name;
  }

  /** Attach on a hidden layer, compile, upload, draw once into 1×1, then show. */
  private warm(e: Entry): void {
    const { renderer, scene } = this;
    const g = e.group;
    const culled: THREE.Object3D[] = [];
    g.traverse((o) => {
      o.layers.set(WARM_LAYER);
      if (o.frustumCulled) {
        o.frustumCulled = false;
        culled.push(o);
      }
      const mats = (o as THREE.Mesh).material;
      if (mats) for (const m of Array.isArray(mats) ? mats : [mats]) for (const t of texturesOf(m)) if (!t.userData.resident) e.textures.add(t);
    });
    scene.add(g);
    scene.traverse((o) => {
      if ((o as THREE.Light).isLight) o.layers.enable(WARM_LAYER);
    });
    const w0 = performance.now();
    renderer.compile(g, this.camera, scene);
    const w1 = performance.now();
    for (const t of e.textures) renderer.initTexture(t);
    const w2 = performance.now();
    const cam = this.warmCam;
    cam.copy(this.camera as THREE.PerspectiveCamera);
    cam.layers.set(WARM_LAYER);
    const prevTarget = renderer.getRenderTarget();
    const autoShadow = renderer.shadowMap.autoUpdate;
    // no shadow pass for the warm draw, unless a shadow map doesn't exist yet (the boot prime, before the first
    // frame): then one pass allocates it, or the receivers would sample an unbound shadow sampler
    let unallocated = false;
    scene.traverse((o) => {
      const l = o as THREE.DirectionalLight;
      if (l.isLight && l.castShadow && l.shadow && !l.shadow.map) unallocated = true;
    });
    renderer.shadowMap.autoUpdate = unallocated;
    renderer.setRenderTarget(this.warmRT);
    renderer.render(scene, cam);
    this.warms.push({ id: e.spec.id, compile: r2(w1 - w0), upload: r2(w2 - w1), draw: r2(performance.now() - w2) });
    if (this.warms.length > 16) this.warms.shift();
    renderer.setRenderTarget(prevTarget);
    renderer.shadowMap.autoUpdate = autoShadow;
    for (const o of culled) o.frustumCulled = true;
    g.traverse((o) => o.layers.set(0));
  }

  private drop(e: Entry): void {
    this.entries.delete(e.spec.id);
    this.scene.remove(e.group);
    if (e.state === "live") {
      this.counts.unloads++;
      e.spec.onDrop?.();
    }
    this.drops.push(e);
  }

  private dispose(e: Entry): void {
    if (this.faults.skipDispose) return;
    const geos = new Set<THREE.BufferGeometry>();
    const mats = new Set<THREE.Material>();
    e.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) geos.add(m.geometry);
      if (m.material) for (const x of Array.isArray(m.material) ? m.material : [m.material]) mats.add(x);
      if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose();
    });
    for (const g of geos) if (!g.userData.resident) g.dispose();
    const props = (this.renderer as unknown as { properties: { get(o: object): { currentProgram?: { cacheKey: string } } } }).properties;
    for (const m of mats) {
      for (const t of texturesOf(m)) if (!t.userData.resident) e.textures.add(t);
      if (m.userData.resident) continue;
      const key = props.get(m).currentProgram?.cacheKey;
      if (key !== undefined && !this.pins.has(key)) {
        this.pins.set(key, m); // this program's pin: kept, so the program outlives the chunk
        continue;
      }
      m.dispose();
    }
    for (const t of e.textures) t.dispose();
    e.textures.clear();
    e.group.clear();
  }

  /** Clear the measurements (not the chunks). */
  resetStats(): void {
    this.slices = [];
    this.cuts = [];
    this.lateLog = [];
    this.counts = { loads: 0, unloads: 0, late: 0, steps: 0 };
    this.maxStep = { ms: 0, name: "" };
  }

  /** Which chunks are live, building or queued, and how far each is from (x, z). */
  chunks(x?: number, z?: number): { id: string; state: State | "unloaded"; d: number | null }[] {
    return this.specs.map((s) => ({ id: s.id, state: this.entries.get(s.id)?.state ?? "unloaded", d: x === undefined || z === undefined ? null : r2(areaDistance(s.area, x, z)) }));
  }

  stats(): Record<string, unknown> {
    const ms = this.slices.map((s) => s.ms).sort((a, b) => a - b);
    const pct = (p: number): number => (ms.length ? ms[Math.min(ms.length - 1, Math.ceil((p / 100) * ms.length) - 1)]! : 0);
    const worst = this.slices.reduce<Slice | null>((w, s) => (!w || s.ms > w.ms ? s : w), null);
    const info = this.renderer.info;
    return {
      primed: this.primed,
      frame: this.frame,
      config: this.cfg,
      live: [...this.entries.values()].filter((e) => e.state === "live").map((e) => e.spec.id),
      pending: [...this.entries.values()].filter((e) => e.state !== "live").map((e) => `${e.spec.id} ${e.i}/${e.steps.length}`),
      dropsQueued: this.drops.length,
      ...this.counts,
      lateLog: this.lateLog,
      slices: {
        n: ms.length,
        maxMs: r2(ms.at(-1) ?? 0),
        p99Ms: r2(pct(99)),
        p50Ms: r2(pct(50)),
        meanMs: r2(ms.reduce((a, b) => a + b, 0) / Math.max(1, ms.length)),
        overBudget: ms.filter((v) => v > this.cfg.budgetMs).length,
        worst: worst ? { frame: worst.frame, ms: r2(worst.ms), steps: worst.steps } : null,
        maxStep: { ms: r2(this.maxStep.ms), name: this.maxStep.name },
        warms: this.warms,
      },
      cuts: this.cuts,
      pins: this.pins.size,
      memory: { geometries: info.memory.geometries, textures: info.memory.textures },
      programs: info.programs?.length ?? 0,
    };
  }
}

/** Every texture a material holds (its maps and texture uniforms). */
function texturesOf(m: THREE.Material): THREE.Texture[] {
  const out: THREE.Texture[] = [];
  for (const v of Object.values(m as unknown as Record<string, unknown>)) if ((v as THREE.Texture)?.isTexture) out.push(v as THREE.Texture);
  return out;
}
